import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { StaffScope } from "@/lib/staffScope";

/** See src/lib/staffScope.ts for what each scope value means. */
export type AgentAppScope  = StaffScope;
export type AgentDocAccess = "download" | "view" | "none";
export type AgentResourceScope = StaffScope;
export type AgentMessageScope = Exclude<StaffScope, "none">;

export interface AgentPermissions {
  applications:   AgentAppScope;
  documents:      AgentDocAccess;
  /**
   * Can upload, replace, and review documents on an applicant's behalf. Distinct
   * from `documents`, which only governs reading/downloading them.
   */
  documents_upload: boolean;
  status_change:  boolean;
  messages_read:  boolean;
  messages_reply: boolean;
  /** Can view the Applicants tab — all registered users, even without applications */
  users_access:   boolean;
  /** Can access the Leads page */
  leads_access:   boolean;
  /** Can send broadcast emails and messages */
  broadcast:      boolean;
  /** Can ghost-login as an applicant/member */
  ghost_login:    boolean;
  /** Can trigger TPC portal sync jobs */
  tpc_sync:        boolean;
  /** Can access active members */
  members_access: AgentResourceScope;
  /** Can access service requests */
  service_requests_access: AgentResourceScope;
  /** Message visibility scope */
  messages_scope: AgentMessageScope;
  /** Can play AI call recordings and read full transcripts (summaries only need messages_read) */
  calls_access:   boolean;
  /** Can use the admin AI assistant (drafts only, never sends) */
  ai_access:      boolean;
  /** Can view the Analytics dashboard (read-only) without full admin */
  analytics_access: boolean;
  /** Can view the Lead Source box — UTM / referrer / affiliate / partner attribution. Hidden by default. */
  lead_source_access: boolean;
  /** Can approve member-ID link requests (registrants linking an existing membership). */
  member_link_approval: boolean;
  /** Partners section read scope. */
  partners_access: AgentResourceScope;
  /** Can register a new partner company */
  partners_create: boolean;
  /** Can edit partners in scope, request documents, reject, suspend. Activation stays admin-only. */
  partners_update: boolean;
  /** Can change a contact's email (incl. login email), phone and address. Opt-in. */
  contact_edit: boolean;
}

export type StaffRole = "admin" | "agent" | null;

/** Predefined presets an admin can apply to any agent */
export const PERMISSION_PRESETS: Record<string, AgentPermissions & { label: string; description: string }> = {
  officer: {
    label: "Officer",
    description: "Full access — all apps, all users, status change, documents",
    applications:   "all",
    documents:      "download",
    documents_upload: true,
    status_change:  true,
    messages_read:  true,
    messages_reply: true,
    users_access:   true,
    leads_access:   true,
    broadcast:      true,
    ghost_login:    true,
    tpc_sync:        true,
    members_access: "all",
    service_requests_access: "all",
    messages_scope: "all",
    calls_access:   true,
    ai_access:      true,
    analytics_access: true,
    lead_source_access: true,
    member_link_approval: true,
    partners_access: "all",
    partners_create: true,
    partners_update: true,
    contact_edit:   true,
  },
  consultant: {
    label: "Consultant",
    description: "Assigned apps + user list, view docs, read/reply messages",
    applications:   "assigned",
    documents:      "view",
    documents_upload: false,
    status_change:  false,
    messages_read:  true,
    messages_reply: true,
    users_access:   true,
    leads_access:   true,
    broadcast:      false,
    ghost_login:    false,
    tpc_sync:        false,
    members_access: "assigned",
    service_requests_access: "assigned",
    messages_scope: "assigned",
    calls_access:   false,
    ai_access:      true,
    analytics_access: false,
    lead_source_access: false,
    member_link_approval: false,
    partners_access: "none",
    partners_create: false,
    partners_update: false,
    contact_edit:   false,
  },
  support: {
    label: "Support",
    description: "User list + messaging only — no documents, no status change",
    applications:   "assigned",
    documents:      "none",
    documents_upload: false,
    status_change:  false,
    messages_read:  true,
    messages_reply: false,
    users_access:   true,
    leads_access:   false,
    broadcast:      false,
    ghost_login:    false,
    tpc_sync:        false,
    members_access: "none",
    service_requests_access: "assigned",
    messages_scope: "assigned",
    calls_access:   false,
    ai_access:      false,
    analytics_access: false,
    lead_source_access: false,
    member_link_approval: false,
    partners_access: "none",
    partners_create: false,
    partners_update: false,
    contact_edit:   false,
  },
};

/** Default permissions given to a new agent with no explicit row in agent_permissions */
const DEFAULT_AGENT_PERMISSIONS: AgentPermissions = {
  ...PERMISSION_PRESETS.consultant,
  documents_upload: false, // opt-in by default
  users_access: false, // opt-in by default
  leads_access: false, // opt-in by default
  broadcast:    false, // opt-in by default
  ghost_login:  false, // opt-in by default
  tpc_sync:     false, // opt-in by default
  calls_access: false, // opt-in by default
  ai_access:    false, // opt-in by default
  partners_access: "none", // opt-in by default
  members_access: "assigned",
  service_requests_access: "assigned",
  messages_scope: "assigned",
};

export function normalizeAgentPermissions(perms?: Partial<AgentPermissions> | null): AgentPermissions {
  return {
    applications: perms?.applications ?? DEFAULT_AGENT_PERMISSIONS.applications,
    documents: perms?.documents ?? DEFAULT_AGENT_PERMISSIONS.documents,
    documents_upload: perms?.documents_upload ?? DEFAULT_AGENT_PERMISSIONS.documents_upload,
    status_change: perms?.status_change ?? DEFAULT_AGENT_PERMISSIONS.status_change,
    messages_read: perms?.messages_read ?? DEFAULT_AGENT_PERMISSIONS.messages_read,
    messages_reply: perms?.messages_reply ?? DEFAULT_AGENT_PERMISSIONS.messages_reply,
    users_access: perms?.users_access ?? DEFAULT_AGENT_PERMISSIONS.users_access,
    leads_access: perms?.leads_access ?? DEFAULT_AGENT_PERMISSIONS.leads_access,
    broadcast: perms?.broadcast ?? DEFAULT_AGENT_PERMISSIONS.broadcast,
    ghost_login: perms?.ghost_login ?? DEFAULT_AGENT_PERMISSIONS.ghost_login,
    tpc_sync: perms?.tpc_sync ?? DEFAULT_AGENT_PERMISSIONS.tpc_sync,
    members_access: perms?.members_access ?? (
      perms?.applications === "all" ? "all" :
      perms?.applications === "none" ? "none" :
      DEFAULT_AGENT_PERMISSIONS.members_access
    ),
    service_requests_access: perms?.service_requests_access ?? DEFAULT_AGENT_PERMISSIONS.service_requests_access,
    messages_scope: perms?.messages_scope ?? DEFAULT_AGENT_PERMISSIONS.messages_scope,
    calls_access: perms?.calls_access ?? DEFAULT_AGENT_PERMISSIONS.calls_access,
    ai_access: perms?.ai_access ?? DEFAULT_AGENT_PERMISSIONS.ai_access,
    analytics_access: perms?.analytics_access ?? DEFAULT_AGENT_PERMISSIONS.analytics_access,
    lead_source_access: perms?.lead_source_access ?? DEFAULT_AGENT_PERMISSIONS.lead_source_access,
    member_link_approval: perms?.member_link_approval ?? DEFAULT_AGENT_PERMISSIONS.member_link_approval,
    partners_access: perms?.partners_access ?? DEFAULT_AGENT_PERMISSIONS.partners_access,
    partners_create: perms?.partners_create ?? DEFAULT_AGENT_PERMISSIONS.partners_create,
    partners_update: perms?.partners_update ?? DEFAULT_AGENT_PERMISSIONS.partners_update,
    contact_edit: perms?.contact_edit ?? DEFAULT_AGENT_PERMISSIONS.contact_edit,
  };
}

interface UseStaffRoleResult {
  role:        StaffRole;
  isAdmin:     boolean;
  isAgent:     boolean;
  isStaff:     boolean; // admin OR agent
  /** Holds the external `partner` role. Never a staff role — isStaff stays false. */
  isPartner:   boolean;
  permissions: AgentPermissions | null; // null for non-staff
  loading:     boolean;
}

/**
 * Returns the current user's staff role (admin | agent | null)
 * and their effective permissions.
 *
 * - Admins get all permissions (no row needed in agent_permissions).
 * - Agents get permissions from agent_permissions (defaults if missing).
 * - Applicants / guests get null.
 */
type StaffAccess = Pick<UseStaffRoleResult, "role" | "isPartner" | "permissions">;

async function fetchStaffAccess(userId: string): Promise<StaffAccess> {
  // Read all roles without filtering by enum values that may not be migrated.
  const { data: roles, error: roleError } = await supabase
    .from("user_roles").select("role").eq("user_id", userId);
  if (roleError) throw roleError;
  // The deployed enum includes partner before regenerated client types do.
  const roleSet = new Set<string>((roles || []).map((entry) => entry.role));
  const isPartner = roleSet.has("partner");
  if (roleSet.has("admin")) {
    return { role: "admin", isPartner, permissions: {
      applications:   "all",
      documents:      "download",
      documents_upload: true,
      status_change:  true,
      messages_read:  true,
      messages_reply: true,
      users_access:   true,
      leads_access:   true,
      broadcast:      true,
      ghost_login:    true,
      tpc_sync:        true,
      members_access: "all",
      service_requests_access: "all",
      messages_scope: "all",
      calls_access:   true,
      ai_access:      true,
      analytics_access: true,
      lead_source_access: true,
      member_link_approval: true,
      partners_access: "all",
      partners_create: true,
      partners_update: true,
      contact_edit:   true,
    } };
  }
  if (roleSet.has("agent") || roleSet.has("support")) {
    const { data: permissions, error } = await supabase
      .from("agent_permissions").select("*").eq("user_id", userId).maybeSingle();
    if (error) throw error;
    return { role: "agent", isPartner, permissions: normalizeAgentPermissions(permissions) };
  }
  return { role: null, isPartner, permissions: null };
}

export function useStaffRole(): UseStaffRoleResult {
  const { user, loading: authLoading } = useAuth();
  const query = useQuery({
    // Shared by layout, route guards and every PartnerBadge in the application
    // list. One request per user, rather than one per rendered row (Sentry 3D).
    queryKey: ["staff-access", user?.id],
    queryFn: () => fetchStaffAccess(user!.id),
    enabled: !!user && !authLoading,
    staleTime: 60_000,
    gcTime: 0, // Discard access data once the user's last observer leaves.
    retry: false,
  });
  // Never expose the previous user's access, or cached privileges after a
  // failed revalidation. Database policies remain the authorization boundary.
  const access = user && !authLoading && !query.isError ? query.data : undefined;
  const role = access?.role ?? null;
  return {
    role,
    isAdmin: role === "admin",
    isAgent: role === "agent",
    isStaff: role === "admin" || role === "agent",
    isPartner: access?.isPartner ?? false,
    permissions: access?.permissions ?? null,
    loading: authLoading || (!!user && query.isPending),
  };
}
